import {
  doc,
  getDoc,
  collection,
  getDocs,
  runTransaction,
  serverTimestamp,
  writeBatch,
  increment,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase";
import { supabase } from "../supabase";
import { syncLedgerOrderAmount, getISTDateString } from "./cashierService";
import { 
  normalizeAndMergeItems, 
  splitOrderToOutlets, 
  calculateLineTotals,
  calculateOrderTotals 
} from "../utils/orderUtils";
import { generateTripSheetPdf } from "../utils/pdfTripSheet";
import { generateChallanPdf } from "../utils/pdfChallan";
import { buildHsnSummary } from "../utils/hsnSummary";

/**
 * Enterprise Service for processing single order approvals.
 * Shared between single-approve and batch-approve flows.
 */
export async function processSingleOrderApproval(
  orderId, 
  { 
    onStep = () => {}, 
    onProgress = () => {},
    // Optional overrides (for single approve page)
    itemsOverride = null,
    vehicleNoOverride = null,
    addressOverride = null,
    selectedOutletIdsOverride = null
  }
) {
  try {
    onStep("Verifying order status...");
    const orderRef = doc(db, "orders", orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) throw new Error("Order not found");
    const order = orderSnap.data();
    
    if (order.status === "APPROVED") return { success: true, status: "ALREADY_APPROVED" };

    onStep("Loading Master Data...");
    // 1. Fetch SKU Master
    const skuSnap = await getDocs(collection(db, "skuMaster"));
    const skuMaster = {};
    skuSnap.forEach(d => {
      const data = d.data();
      if (data.sku) skuMaster[data.sku.trim().toUpperCase()] = data;
    });

    // 2. Fetch Party Master
    let partySnapData = {};
    let partyPhone = "";
    let resolvedPaymentMode = "Cash";
    if (order.partyId) {
      const pSnap = await getDoc(doc(db, "partyMaster", order.partyId.trim()));
      if (pSnap.exists()) {
        partySnapData = pSnap.data();
        partyPhone = partySnapData.phone || "";
        resolvedPaymentMode = (partySnapData.payment_mode || order.payment_mode || "Cash").trim();
      } else {
        resolvedPaymentMode = (order.payment_mode || "Cash").trim();
      }
    }
    if (partyPhone && partyPhone.length === 10) partyPhone = "91" + partyPhone;

    const finalPartyMetadata = {
      partyPhone,
      partyType: partySnapData.partytype || "",
      creditLimit: Number(partySnapData.creditLimit || 0),
      dmsName: partySnapData.dms_name || "",
      easyCigName: partySnapData.easycig_name || "",
      gstno: partySnapData.gstno || "",
      route: partySnapData.route || "",
      salesman: partySnapData.salesman || ""
    };

    // 3. Fetch Company & Outlets
    const cSnap = await getDoc(doc(db, "companyInfo", "default"));
    const company = cSnap.exists() ? cSnap.data() : null;

    const qOutlets = query(
      collection(db, "outletMaster"), 
      where("partyId", "==", order.partyId), 
      where("status", "==", "active")
    );
    const snapOutlets = await getDocs(qOutlets);
    const outlets = snapOutlets.docs.map(d => ({ id: d.id, ...d.data() }));

    // 4. Resolve Items & Totals
    const finalItems = itemsOverride || order.items || {};
    const mergedItems = normalizeAndMergeItems(finalItems);
    const totals = calculateOrderTotals(mergedItems, skuMaster);
    const orderValue = totals.tGrand;
    const finalVehicleNo = vehicleNoOverride || order.vehicleNo || partySnapData.vehicleno || "";
    const finalAddress = addressOverride || partySnapData.address || "";
    const approvedDateOnly = getISTDateString();

    onStep("Validating outlets...");
    const outletIdsToUse = selectedOutletIdsOverride || outlets.map(o => o.id);
    const selectedOutletsData = outlets.filter(o => outletIdsToUse.includes(o.id));

    if (outlets.length > 0) {
      const totalAvailableLimit = selectedOutletsData.reduce((acc, o) => {
        const perInvoiceLimit = Number(o.dailyInvoiceLimit) || 0;
        const annualLimitMax = Number(o.annualLimit) || 0;
        const currentAnnualUsed = Number(o.annualUsed || 0);
        const effectiveLimit = Math.min(perInvoiceLimit, Math.max(0, annualLimitMax - currentAnnualUsed));
        return acc + effectiveLimit;
      }, 0);

      if (orderValue > (totalAvailableLimit + 1)) {
        throw new Error(`Insufficient Outlet Capacity: Order (₹${orderValue.toFixed(2)}) > Outlets (₹${totalAvailableLimit.toFixed(2)})`);
      }
    }

    onStep("Acquiring Invoice Sequence...");
    // 1. Calculate Period Code (e.g. 2627 for FY 2026-27)
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const dateCode = currentYear.toString().slice(-2) + 
                     currentMonth.toString().padStart(2, "0") + 
                     now.getDate().toString().padStart(2, "0");

    let periodCode;
    if (currentMonth >= 4) {
      periodCode = `${currentYear.toString().slice(-2)}${(currentYear + 1).toString().slice(-2)}`;
    } else {
      periodCode = `${(currentYear - 1).toString().slice(-2)}${currentYear.toString().slice(-2)}`;
    }

    // 2. Pre-calculate Outlet Splits to know how many IDs we need
    const itemsByOutlet = {}; 
    let activeOutletsCount = 0;
    const finalOutletOrdersToCreate = [];

    if (selectedOutletsData.length > 0) {
      Object.entries(mergedItems).forEach(([sku, qty]) => {
        const m = skuMaster[sku] || {};
        const splits = splitOrderToOutlets(qty, m.minQty || 1, selectedOutletsData.map(o => ({ id: o.id, remainingLimit: 999999 })), Number(m.Basic_Eway || 0));
        splits.forEach((q, i) => {
          if (!itemsByOutlet[i]) itemsByOutlet[i] = {};
          if (q > 0) itemsByOutlet[i][sku] = q;
        });
      });

      selectedOutletsData.forEach((outlet, i) => {
        const oItems = itemsByOutlet[i] || {};
        const oTotals = calculateOrderTotals(oItems, skuMaster);
        if (oTotals.tQty > 0) {
          activeOutletsCount++;
          finalOutletOrdersToCreate.push({ outlet, oItems, oTotals });
        }
      });
    }

    // 3. Unified Transaction for Order ID and Invoice Numbers
    const { seq, invStart } = await runTransaction(db, async (tx) => {
      // --- READ PHASE ---
      const counterRef = doc(db, "counters", "orderSequence");
      const snap = await tx.get(counterRef);

      const invCounterRef = doc(db, "counters", `outletSequence_${periodCode}`);
      const invSnap = await tx.get(invCounterRef);

      // --- WRITE PHASE ---
      let nextRoot = 1;
      if (snap.exists() && snap.data().date === dateCode) nextRoot = snap.data().lastSeq + 1;
      tx.set(counterRef, { date: dateCode, lastSeq: nextRoot }, { merge: true });

      let nextInv = 1;
      if (activeOutletsCount > 0) {
        if (invSnap.exists()) nextInv = (invSnap.data().lastSeq || 0) + 1;
        tx.set(invCounterRef, { lastSeq: (nextInv + activeOutletsCount - 1) }, { merge: true });
      }

      return { seq: nextRoot, invStart: nextInv };
    });
    const clientPrefix = (order.clientId || "MD").slice(0, 2).toUpperCase();
    const finalOrderId = `${clientPrefix}-${dateCode}-${String(seq).padStart(4, "0")}`;

    onStep("Generating PDFs...");
    // Build snapshot array for root order
    const legacyOrderLines = Object.entries(mergedItems).map(([sku, qty], idx) => {
      const m = skuMaster[sku] || {};
      const line = calculateLineTotals(m, qty);
      return {
        lineNo: idx + 1,
        ...line,
        sku,
        skuName: m.Name || sku,
        itemCode: m.ItemCode || "",
        msku: m.msku || "",
        segment: m.segment || "",
        brand: m.Brand || "",
        description: m.description || "",
        vehicleNo: finalVehicleNo,
        // Calculated field names requested by user
        ewayAmount: line.ewayLineTotal,
        totalAmount: line.lineTotal,
        rate: line.basicRate,
        rebatePerUnit: line.rebatePerUnit
      };
    });

    const tripPdf = generateTripSheetPdf(
      finalOrderId, 
      { ...order, displayOrderId: finalOrderId, orderDateOnly: approvedDateOnly, items: mergedItems, orderLines: legacyOrderLines }, 
      mergedItems, 
      skuMaster, 
      { 
        totalQty: totals.tQty, 
        grandTotal: totals.tGrand 
      }, 
      "Trip Sheet"
    );
    const hsnSummary = buildHsnSummary(mergedItems, skuMaster);
    const challanPdf = generateChallanPdf(
      finalOrderId, 
      { ...order, displayOrderId: finalOrderId, orderDateOnly: approvedDateOnly, vehicleNo: finalVehicleNo, items: mergedItems, orderLines: legacyOrderLines }, 
      hsnSummary, 
      { 
        totalQty: totals.tQty,
        totalBasic: totals.tBasic,
        totalGST: totals.tGST,
        totalCESS: totals.tCess,
        totalAddlCess: totals.tAddl,
        grandTotal: totals.tEway 
      }, 
      company
    );

    const tripUrl = await uploadPdfToSupabase("tripsheets", `tripsheets/TripSheet-${finalOrderId}.pdf`, tripPdf.output("blob"));
    const challanUrl = await uploadPdfToSupabase("challans", `Challan-${finalOrderId}.pdf`, challanPdf.output("blob"));

    onStep("Preparing database updates...");
    const preparatoryOps = [];
    const finalizingOps = [];
    
    // Line items for separate collection
    legacyOrderLines.forEach(line => {
      preparatoryOps.push({
        type: 'set',
        ref: doc(collection(db, "orderLines")),
        data: {
          clientId: order.clientId || company?.clientId || "",
          ...line,
          orderId,
          displayOrderId: finalOrderId,
          approvedAt: new Date(),
          approvedDateOnly,
          partyId: order.partyId,
          partyName: order.partyName,
          ...finalPartyMetadata,
          payment_mode: resolvedPaymentMode,
          source: order.source || "web",
          status: "APPROVED"
        }
      });
    });

    // Outlet Orders
    if (finalOutletOrdersToCreate.length > 0) {
      finalOutletOrdersToCreate.forEach((orderData, i) => {
        const { outlet, oItems, oTotals } = orderData;
        const currentInvSeq = invStart + i;
        const outletBillingId = `INV-${periodCode}-${String(currentInvSeq).padStart(4, "0")}`;
        
        const linesArray = [];
        Object.entries(oItems).forEach(([sku, q]) => {
          const m = skuMaster[sku] || {};
          const line = calculateLineTotals(m, q);
          linesArray.push({
            sku,
            skuName: m.Name || sku,
            qty: q,
            ...line
          });
        });

        preparatoryOps.push({
          type: 'set',
          ref: doc(collection(db, "outletOrders")),
          data: {
            clientId: order.clientId || "",
            status: "APPROVED",
            approvedAt: serverTimestamp(),
            approvedDateOnly,
            displayOrderId: outletBillingId,
            rootOrderId: orderId,
            rootDisplayOrderId: finalOrderId,
            partyId: order.partyId,
            partyName: order.partyName,
            partyPhone,
            payment_mode: resolvedPaymentMode,
            outletId: outlet.id,
            outletName: outlet.outletName,
            ...finalPartyMetadata,
            totalQty: oTotals.tQty,
            totalAmount: oTotals.tGrand,
            ewayAmount: oTotals.tEway,
            rate: oTotals.tBasic,
            tripSheetPdfUrl: tripUrl,
            challanPdfUrl: challanUrl,
            vehicleNo: finalVehicleNo,
            lines: linesArray
          }
        });
        finalizingOps.push({
          type: 'update',
          ref: doc(db, "outletMaster", outlet.id),
          data: { annualUsed: increment(oTotals.tEway) }
        });
      });
    }

    // Daily Stock Deduction
    Object.entries(mergedItems).forEach(([sku, qty]) => {
      preparatoryOps.push({
        type: 'set',
        ref: doc(db, "daily_stock", `${approvedDateOnly}_${sku}`),
        data: {
          clientId: order.clientId || company?.clientId || "",
          date: approvedDateOnly,
          sku: sku,
          approved: increment(qty)
        },
        options: { merge: true }
      });
    });

    // Root Order Update
    finalizingOps.push({
      type: 'update',
      ref: orderRef,
      data: {
        status: "APPROVED",
        displayOrderId: finalOrderId,
        approvedAt: serverTimestamp(),
        approvedDateOnly,
        items: mergedItems,
        orderLines: legacyOrderLines,
        ...finalPartyMetadata,
        payment_mode: resolvedPaymentMode,
        totalQty: totals.tQty,
        totalAmount: totals.tGrand,
        ewayAmount: totals.tEway,
        rate: totals.tBasic,           // ✅ Added: Total Taxable Value
        vehicleNo: finalVehicleNo,    // ✅ Added
        address: finalAddress,        // ✅ Added
        gstin: order.gstin || finalPartyMetadata.gstno || "", // ✅ Added
        ledgerSynced: false,          // ✅ Added for Settlement boundary
        orderDate: order.createdAt || serverTimestamp(), // ✅ Added for history
        tripSheetPdfUrl: tripUrl,
        challanPdfUrl: challanUrl,
        updatedAt: serverTimestamp()
      }
    });

    onStep("Committing transactions...");
    const allOps = [...preparatoryOps, ...finalizingOps];
    let batch = writeBatch(db);
    let count = 0;
    for (let i = 0; i < allOps.length; i++) {
      const op = allOps[i];
      if (op.type === 'set') {
        if (op.options) {
          batch.set(op.ref, op.data, op.options);
        } else {
          batch.set(op.ref, op.data);
        }
      }
      else if (op.type === 'update') batch.update(op.ref, op.data);
      
      count++;
      onProgress(Math.round(((i + 1) / allOps.length) * 100));
      if (count >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }
    if (count > 0) await batch.commit();

    onStep("Syncing ledger...");
    await syncLedgerOrderAmount(order.partyId, approvedDateOnly);

    return { success: true, orderId: finalOrderId, tripUrl, challanUrl };
  } catch (err) {
    console.error(`Error approving order ${orderId}:`, err);
    return { success: false, error: err.message };
  }
}

async function uploadPdfToSupabase(bucket, filePath, blob) {
  const { error } = await supabase.storage.from(bucket).upload(filePath, blob, { contentType: "application/pdf", upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}
